import { http } from '../client';
import type { CallOptions, WriteOptions } from '../types';
import type { CommerceApi } from './commerce';

const seg = encodeURIComponent;
const rd = (o?: CallOptions) => ({ signal: o?.signal });
const wr = (o?: WriteOptions) => ({ signal: o?.signal, idempotencyKey: o?.idempotencyKey, notifyCustomer: o?.notifyCustomer });

export const commerceHttp: CommerceApi = {
  getBillingOverview: (opts) => http.get('api', '/commerce/billing/overview', rd(opts)),
  listQuotes: (q, opts) =>
    http.get('api', '/commerce/quotes', {
      query: { customer_id: q.customer_id, domain: q.domain, operation: q.operation, limit: q.limit, before: q.before },
      ...rd(opts),
    }),
  getQuote: (id, opts) => http.get('api', `/commerce/quotes/${seg(id)}`, rd(opts)),
  listOrders: (q, opts) =>
    http.get('api', '/commerce/orders', { query: { status: q.status, customer_id: q.customer_id, limit: q.limit, before: q.before }, ...rd(opts) }),
  getOrder: (id, opts) => http.get('api', `/commerce/orders/${seg(id)}`, rd(opts)),
  listHolds: (q, opts) => http.get('api', '/commerce/holds', { query: { status: q.status, purpose: q.purpose, limit: q.limit, before: q.before }, ...rd(opts) }),
  getHold: (id, opts) => http.get('api', `/commerce/holds/${seg(id)}`, rd(opts)),
  listPayments: (q, opts) =>
    http.get('api', '/commerce/payments', {
      query: { status: q.status, provider: q.provider, purpose: q.purpose, customer_id: q.customer_id, limit: q.limit, before: q.before },
      ...rd(opts),
    }),
  getPayment: (id, opts) => http.get('api', `/commerce/payments/${seg(id)}`, rd(opts)),
  listRefunds: (q, opts) => http.get('api', '/commerce/refunds', { query: { status: q.status, limit: q.limit, before: q.before }, ...rd(opts) }),
  getRefund: (id, opts) => http.get('api', `/commerce/refunds/${seg(id)}`, rd(opts)),
  listInvoices: (q, opts) =>
    http.get('api', '/commerce/invoices', {
      query: { document_type: q.document_type, series: q.series, customer_id: q.customer_id, limit: q.limit, before: q.before },
      ...rd(opts),
    }),
  getInvoice: (id, opts) => http.get('api', `/commerce/invoices/${seg(id)}`, rd(opts)),
  getLedgerSummary: (opts) => http.get('api', '/commerce/ledger/summary', rd(opts)),
  listLedgerAccounts: (q, opts) =>
    http.get('api', '/commerce/ledger/accounts', {
      query: { owner_type: q.owner_type, account_type: q.account_type, currency: q.currency, q: q.q, limit: q.limit, after: q.after },
      ...rd(opts),
    }),
  getLedgerAccount: (id, opts) => http.get('api', `/commerce/ledger/accounts/${seg(id)}`, rd(opts)),
  listJournalEntries: (q, opts) =>
    http.get('api', '/commerce/ledger/journal', {
      query: { entry_type: q.entry_type, resource_type: q.resource_type, resource_id: q.resource_id, account_id: q.account_id, limit: q.limit, before: q.before },
      ...rd(opts),
    }),
  getJournalEntry: (id, opts) => http.get('api', `/commerce/ledger/journal/${seg(id)}`, rd(opts)),
  getPricing: (q, opts) => http.get('api', '/commerce/pricing', { query: { history: q.history || undefined }, ...rd(opts) }),
  listTaxCodes: (opts) => http.get('api', '/commerce/pricing/tax-codes', rd(opts)),
  getPriceBook: (id, opts) => http.get('api', `/commerce/pricing/books/${seg(id)}`, rd(opts)),
  getPriceBookMatrix: (id, q, opts) => http.get('api', `/commerce/pricing/books/${seg(id)}/matrix`, { query: { tld_id: q.tld_id }, ...rd(opts) }),
  listPromotions: (opts) => http.get('api', '/commerce/promotions', rd(opts)),
  getPromotion: (id, opts) => http.get('api', `/commerce/promotions/${seg(id)}`, rd(opts)),
  listBankTransactions: (q, opts) =>
    http.get('api', '/bank-transactions', { query: { status: q.status, source: q.source, q: q.q, limit: q.limit, before: q.before }, ...rd(opts) }),
  getBankTransaction: (id, opts) => http.get('api', `/bank-transactions/${seg(id)}`, rd(opts)),
  assignBankTransaction: (id, body, opts) => http.post('api', `/bank-transactions/${seg(id)}/assign`, body, wr(opts)),
  creditBankTransaction: (id, body, opts) => http.post('api', `/bank-transactions/${seg(id)}/credit`, body, wr(opts)),
  dismissBankTransaction: (id, body, opts) => http.post('api', `/bank-transactions/${seg(id)}/dismiss`, body, wr(opts)),
  listPaymentRequests: (q, opts) =>
    http.get('api', '/payment-requests', {
      query: { status: q.status, customer_id: q.customer_id, order_id: q.order_id, attention: q.attention, limit: q.limit, before: q.before },
      ...rd(opts),
    }),
  getPaymentRequest: (id, opts) => http.get('api', `/payment-requests/${seg(id)}`, rd(opts)),
  createPaymentRequest: (body, opts) => http.post('api', '/payment-requests', body, wr(opts)),
  cancelPaymentRequest: (id, body, opts) => http.post('api', `/payment-requests/${seg(id)}/cancel`, body, wr(opts)),
  confirmPaymentRequest: (id, body, opts) => http.post('api', `/payment-requests/${seg(id)}/confirm`, body, wr(opts)),
  syncPaymentSession: (id, opts) => http.post('api', `/payment-sessions/${seg(id)}/sync`, {}, wr(opts)),
  settlementPayout: (id, body, opts) => http.post('api', `/bank-transactions/${seg(id)}/settlement-payout`, body, wr(opts)),
  refundPaymentSession: (id, body, opts) => http.post('api', `/payment-sessions/${seg(id)}/refunds`, body, wr(opts)),
  listPaymentRequestRefunds: (id, opts) => http.get('api', `/payment-requests/${seg(id)}/refunds`, rd(opts)),
  resolveSourceRefund: (id, body, opts) => http.post('api', `/payment-refunds/${seg(id)}/resolve`, body, wr(opts)),
};
